import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import { apiService } from '../services/api';
import PlayingXIView, { type TeamPlayerDisplay } from '../components/PlayingXIView';
import PlayerPickerModal from '../components/PlayerPickerModal';
import FantasyPointsSummary from '../components/FantasyPointsSummary';

interface H2HLeague {
  id: number;
  name: string;
  league_type: string;
  live_tournament_id: number;
  tournament_name: string;
  max_participants: number;
  current_participants: number;
  status: string;
  h2h_status?: string;
  is_public: boolean;
  join_code?: string;
  passcode?: string;
  created_by: number;
  created_at: string;
  is_creator: boolean;
}

interface Participant {
  id: number;
  user_id: number;
  display_name: string;
  role: string;
  joined_at: string;
}

// Role limits - same as Budget League (must match backend BUDGET_LEAGUE_CONFIG.ROLE_RESTRICTIONS)
const ROLE_MIN: Record<string, number> = {
  'WK-Batsman': 1, 'Batsman': 3, 'Bowler': 3, 'All-Rounder': 1
};
const ROLE_MAX: Record<string, number> = {
  'WK-Batsman': 4, 'Batsman': 6, 'Bowler': 6, 'All-Rounder': 4
};
const MAX_FOREIGN_PLAYERS = 4;

/** Build short team code (e.g. "Chennai Super Kings" -> "CSK"). */
function toTeamCode(teamName: string): string {
  const words = teamName
    .trim()
    .split(/\s+/)
    .filter((w) => w && !['the', 'of', 'and'].includes(w.toLowerCase()));
  if (words.length === 0) return teamName;
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words.map((w) => w.charAt(0).toUpperCase()).join('');
}

/** Compact match label for My Team nav (e.g. "MI vs CSK"). */
function formatMyTeamMatchLabel(match: any, fallbackLabel: string): string {
  const raw = String(match?.match_name ?? '').trim();
  if (!raw) return fallbackLabel;
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  const teamsPart = parts[0] || raw;
  const ordinalPart = parts[1] || '';
  const teams = teamsPart.split(/\s+vs\.?\s+/i).map((s) => s.trim()).filter(Boolean);
  if (teams.length === 2) {
    const shortTeams = `${toTeamCode(teams[0])} vs ${toTeamCode(teams[1])}`;
    return ordinalPart ? `${shortTeams}, ${ordinalPart}` : shortTeams;
  }
  return parts.slice(0, 2).join(', ');
}

/** My Team tab: select match, load players + staging, build 11 with C/VC, save via budget staging (no boosters). */
function H2HMyTeamTab({
  leagueId,
  tournamentId,
  userId,
  onNotify,
  h2hStatus
}: {
  leagueId: number;
  tournamentId: number;
  userId: number | null;
  onNotify: (msg: string) => void;
  h2hStatus?: string;
}) {
  const [state, setState] = useState<{ matches: any[]; my_team_id: number | null } | null>(null);
  const [stateLoading, setStateLoading] = useState(true);
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [staging, setStaging] = useState<any>(null);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staged, setStaged] = useState<any[]>([]);
  const [captainId, setCaptainId] = useState<number | null>(null);
  const [viceCaptainId, setViceCaptainId] = useState<number | null>(null);
  const [teamViewMode, setTeamViewMode] = useState<'list' | 'field'>('list');
  const [showPlayerPicker, setShowPlayerPicker] = useState(false);
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [hasStagedChanges, setHasStagedChanges] = useState(false);
  const [foreignPlayerIds, setForeignPlayerIds] = useState<string[]>([]);

  useEffect(() => {
    if (!leagueId || !userId) return;
    setStateLoading(true);
    apiService.getH2hLeagueState(leagueId)
      .then((res: any) => {
        setState({ matches: res.matches || [], my_team_id: res.my_team_id ?? null });
        if (res.matches?.length && !selectedMatchId) setSelectedMatchId(String(res.matches[0].match_id));
      })
      .catch(() => setState(null))
      .finally(() => setStateLoading(false));
  }, [leagueId, userId]);

  useEffect(() => {
    apiService.getH2hLeagueConfig?.().then((c: any) => setConfig(c || null)).catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    if (!selectedMatchId || !state?.my_team_id) {
      setPlayers([]);
      setStaging(null);
      setStaged([]);
      setCaptainId(null);
      setViceCaptainId(null);
      return;
    }
    setLoadingMatch(true);
    const teamId = Number(state.my_team_id);
    const matchId = String(selectedMatchId);
    Promise.all([
      apiService.getH2hLeaguePlayers(leagueId, matchId),
      apiService.getTeamStagingState(teamId, matchId)
    ])
      .then(([playersRes, stagingRes]: any[]) => {
        const pl = (playersRes?.players || []).map((p: any) => ({
          ...p,
          id: p.id ?? p.tournament_player_id,
          tournament_player_id: p.tournament_player_id ?? p.id
        }));
        setPlayers(pl);
        setForeignPlayerIds(Array.isArray(playersRes?.foreign_player_ids) ? playersRes.foreign_player_ids : []);
        const st = stagingRes?.staging;
        setStaging(st);
        const raw = st?.staged_players;
        const list = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw || '[]') : []);
        setStaged(list.map((p: any) => ({ ...p, tournament_player_id: p.tournament_player_id ?? p.id })));
        setCaptainId(st?.captain_id ?? null);
        setViceCaptainId(st?.vice_captain_id ?? null);
        setHasStagedChanges(false);
      })
      .catch(() => {
        setPlayers([]);
        setStaging(null);
        setStaged([]);
        setHasStagedChanges(false);
      })
      .finally(() => setLoadingMatch(false));
  }, [selectedMatchId, state?.my_team_id, leagueId]);

  const refetchStaging = () => {
    if (!selectedMatchId || !state?.my_team_id) return;
    const teamId = Number(state.my_team_id);
    const matchId = String(selectedMatchId);
    apiService.getTeamStagingState(teamId, matchId)
      .then((res: any) => {
        const st = res?.staging;
        const raw = st?.staged_players;
        const list = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw || '[]') : []);
        setStaged(list.map((p: any) => ({ ...p, tournament_player_id: p.tournament_player_id ?? p.id })));
        setCaptainId(st?.captain_id ?? null);
        setViceCaptainId(st?.vice_captain_id ?? null);
        setHasStagedChanges(false);
      })
      .catch(() => {});
  };

  const matchLocked = state?.matches?.find((m: any) => String(m.match_id) === String(selectedMatchId))?.is_locked ?? false;
  const creditsCap = config?.DEFAULT_BUDGET ?? 100;
  const totalCredits = staged.reduce((sum, p) => sum + (parseFloat(p.credits) || 0), 0);
  const isPlayerForeign = (p: any) => foreignPlayerIds.includes(String(p?.player_id ?? ''));
  const foreignCountInStaged = staged.filter((p: any) => isPlayerForeign(p)).length;
  const canAddUnderForeignLimit = (currentStaged: any[], player: any): boolean => {
    const count = currentStaged.filter((p: any) => isPlayerForeign(p)).length;
    if (isPlayerForeign(player)) return count < MAX_FOREIGN_PLAYERS;
    return true;
  };
  const canSave = staged.length === 11 && captainId != null && viceCaptainId != null && totalCredits <= creditsCap && foreignCountInStaged <= MAX_FOREIGN_PLAYERS && !matchLocked && !saving;

  const handleSave = async () => {
    if (!state?.my_team_id || !selectedMatchId || !canSave) return;
    const teamId = Number(state.my_team_id);
    const matchId = String(selectedMatchId);
    if (Number.isNaN(teamId) || !matchId) return;
    setSaving(true);
    try {
      const capId = captainId != null ? Number(captainId) : null;
      const vcId = viceCaptainId != null ? Number(viceCaptainId) : null;
      const payload = staged.map((p: any) => ({
        tournament_player_id: p.tournament_player_id ?? p.id,
        player_id: p.player_id,
        name: p.name ?? p.player_name,
        role: p.role,
        team: p.team ?? p.team_name,
        credits: p.credits,
        is_captain: (p.tournament_player_id ?? p.id) === capId,
        is_vice_captain: (p.tournament_player_id ?? p.id) === vcId
      }));
      const data = await apiService.updateTeamStagingState(teamId, matchId, payload, capId, vcId) as any;
      onNotify('Team saved.');
      setIsEditingTeam(false);
      setHasStagedChanges(false);
      // Update from response so we don't depend on refetch (H2H uses same staging API as Budget)
      const saved = data?.staged_players;
      if (Array.isArray(saved) && saved.length > 0) {
        setStaged(saved.map((p: any) => ({ ...p, tournament_player_id: p.tournament_player_id ?? p.id })));
      } else {
        refetchStaging();
      }
    } catch (e: any) {
      const msg = e?.message || (e?.response?.data?.message) || 'Save failed.';
      onNotify(msg);
    } finally {
      setSaving(false);
    }
  };

  const canAddPlayerForComposition = (filled: any[], newPlayerRole: string): boolean => {
    const count: Record<string, number> = { 'WK-Batsman': 0, 'Batsman': 0, 'All-Rounder': 0, 'Bowler': 0 };
    filled.forEach((p: any) => { count[p.role] = (count[p.role] ?? 0) + 1; });
    count[newPlayerRole] = (count[newPlayerRole] ?? 0) + 1;
    if ((count[newPlayerRole] ?? 0) > (ROLE_MAX[newPlayerRole] ?? 4)) return false;
    const remaining = 11 - filled.length - 1;
    let minNeededSum = 0;
    (['WK-Batsman', 'Batsman', 'All-Rounder', 'Bowler'] as const).forEach(role => {
      const need = Math.max(0, (ROLE_MIN[role] ?? 0) - (count[role] ?? 0));
      minNeededSum += need;
    });
    return remaining >= minNeededSum;
  };

  const addPlayer = (p: any) => {
    const tid = p.tournament_player_id ?? p.id;
    if (staged.some((s: any) => (s.tournament_player_id ?? s.id) === tid)) return;
    if (staged.length >= 11) return;
    const role = p.role ?? '';
    const playerCredits = parseFloat(p.credits) || 0;
    if (totalCredits + playerCredits > creditsCap) {
      onNotify('Cannot add: would exceed credit limit.');
      return;
    }
    if (!canAddPlayerForComposition(staged, role)) {
      onNotify(`Cannot add: team would exceed role limits (e.g. max ${ROLE_MAX[role] ?? 4} ${role}, or min roles for others not met).`);
      return;
    }
    if (!canAddUnderForeignLimit(staged, p)) {
      onNotify(`Maximum ${MAX_FOREIGN_PLAYERS} foreign players allowed. You have ${foreignCountInStaged} in your team.`);
      return;
    }
    setHasStagedChanges(true);
    const nextLen = staged.length + 1;
    setStaged([...staged, { ...p, tournament_player_id: tid }]);
    if (nextLen >= 11) setShowPlayerPicker(false);
  };
  const removePlayer = (player: TeamPlayerDisplay) => {
    const pid = player.tournament_player_id ?? player.id;
    const next = staged.filter((s: any) => (s.tournament_player_id ?? s.id) !== pid);
    setStaged(next);
    if (pid === captainId) setCaptainId(null);
    if (pid === viceCaptainId) setViceCaptainId(null);
    setHasStagedChanges(true);
  };
  const setCaptain = (tid: number) => {
    setCaptainId(tid);
    if (viceCaptainId === tid) setViceCaptainId(null);
    setHasStagedChanges(true);
  };
  const setViceCaptain = (tid: number) => {
    setViceCaptainId(tid);
    if (captainId === tid) setCaptainId(null);
    setHasStagedChanges(true);
  };

  const displayPlayers: TeamPlayerDisplay[] = staged.map((p: any) => ({
    tournament_player_id: Number(p.tournament_player_id ?? p.id),
    id: p.id,
    player_id: p.player_id,
    name: p.name ?? p.player_name ?? '',
    player_name: p.player_name,
    role: p.role ?? '',
    team: p.team ?? p.team_name ?? '',
    team_name: p.team_name,
    credits: parseFloat(String(p.credits ?? 9)),
    is_captain: (p.tournament_player_id ?? p.id) === captainId,
    is_vice_captain: (p.tournament_player_id ?? p.id) === viceCaptainId,
    isForeign: isPlayerForeign(p)
  }));
  const emptySlotsCount = Math.max(0, 11 - staged.length);
  const availableToPick = players.filter((pl: any) => !staged.some((s: any) => (s.tournament_player_id ?? s.id) === (pl.tournament_player_id ?? pl.id)));

  if (stateLoading || !state) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  if (!state.my_team_id) {
    return <p className="text-gray-600 py-6">Your team slot is being set up. Please refresh the page.</p>;
  }
  const matchOptions = state.matches || [];
  const currentMatch = matchOptions.find((m: any) => String(m.match_id) === String(selectedMatchId));
  const matchIndex = matchOptions.findIndex((m: any) => String(m.match_id) === String(selectedMatchId));

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-primary flex items-center"><span className="mr-3">👥</span>My Team</h2>

      {h2hStatus === 'OPEN' && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          Waiting for an opponent to join. You can build and save your team now — it will be locked once the match starts.
        </div>
      )}

      {/* Match navigation bar - same style as Budget League */}
      {matchOptions.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => {
                if (matchIndex > 0) setSelectedMatchId(String(matchOptions[matchIndex - 1].match_id));
              }}
              disabled={matchIndex <= 0}
              className={`px-3 py-1 rounded-lg text-sm font-semibold ${matchIndex <= 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
            >
              &lt;&lt;
            </button>
            <div className="flex-1 mx-4 text-center">
              <div className="text-lg font-bold text-gray-800 mb-1">
                {formatMyTeamMatchLabel(currentMatch, selectedMatchId || 'Match')}
              </div>
              {matchLocked && <span className="px-2 py-0.5 rounded text-sm font-medium bg-amber-100 text-amber-800">Locked</span>}
            </div>
            <button
              type="button"
              onClick={() => {
                if (matchIndex >= 0 && matchIndex < matchOptions.length - 1) setSelectedMatchId(String(matchOptions[matchIndex + 1].match_id));
              }}
              disabled={matchIndex < 0 || matchIndex >= matchOptions.length - 1}
              className={`px-3 py-1 rounded-lg text-sm font-semibold ${matchIndex < 0 || matchIndex >= matchOptions.length - 1 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
            >
              &gt;&gt;
            </button>
          </div>
        </div>
      )}

      {loadingMatch ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div></div>
      ) : (
        <>
          {/* Credits bar - same style as Budget League */}
          <div className={`rounded-lg p-4 mb-6 border-2 ${matchLocked ? 'bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Credits:</span>
                  <span className={`font-bold ${totalCredits <= creditsCap ? 'text-green-600' : 'text-red-600'}`}>
                    {totalCredits.toFixed(1)}/{creditsCap}
                  </span>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {!matchLocked && (staged.length < 11 || hasStagedChanges) && (
                    <button
                      onClick={handleSave}
                      disabled={!canSave || saving}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  )}
                  {!matchLocked && staged.length === 11 && !hasStagedChanges && (
                    <button
                      type="button"
                      onClick={() => setIsEditingTeam(true)}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
              {staged.length > 0 && foreignCountInStaged > MAX_FOREIGN_PLAYERS && (
                <p className="text-red-600 font-medium text-sm mt-2">
                  Maximum {MAX_FOREIGN_PLAYERS} foreign players allowed. You have {foreignCountInStaged}. Remove {foreignCountInStaged - MAX_FOREIGN_PLAYERS} foreign player(s) to save.
                </p>
              )}
            </div>
          </div>

          {/* Captain & Vice-Captain Selection - same as Budget League (dropdowns); only editable when editing or building */}
          {displayPlayers.length > 0 && (
            <div className="mb-6 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-primary mb-3">Captain & Vice-Captain Selection</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Captain *</label>
                  <select
                    value={captainId ?? ''}
                    onChange={(e) => {
                      const id = e.target.value ? parseInt(e.target.value, 10) : null;
                      setCaptainId(id);
                      if (id !== null && id === viceCaptainId) setViceCaptainId(null);
                    }}
                    disabled={matchLocked || (staged.length === 11 && !isEditingTeam && !hasStagedChanges)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                  >
                    <option value="">Select Captain</option>
                    {displayPlayers.map((p) => {
                      const pid = p.tournament_player_id ?? p.id;
                      const name = p.name ?? p.player_name ?? '–';
                      const cr = Number(p.credits) === Math.floor(p.credits) ? p.credits : (p.credits as number).toFixed(1);
                      return (
                        <option key={pid} value={pid}>
                          {name} ({cr} cr)
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Vice-Captain *</label>
                  <select
                    value={viceCaptainId ?? ''}
                    onChange={(e) => {
                      const id = e.target.value ? parseInt(e.target.value, 10) : null;
                      setViceCaptainId(id);
                      if (id !== null && id === captainId) setCaptainId(null);
                    }}
                    disabled={matchLocked || (staged.length === 11 && !isEditingTeam && !hasStagedChanges)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                  >
                    <option value="">Select Vice-Captain</option>
                    {displayPlayers.map((p) => {
                      const pid = p.tournament_player_id ?? p.id;
                      const name = p.name ?? p.player_name ?? '–';
                      const cr = Number(p.credits) === Math.floor(p.credits) ? p.credits : (p.credits as number).toFixed(1);
                      return (
                        <option key={pid} value={pid}>
                          {name} ({cr} cr)
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              {!matchLocked && (staged.length < 11 || isEditingTeam || hasStagedChanges) && (captainId == null || viceCaptainId == null) && (
                <p className="text-red-600 font-medium text-sm mt-2">* Please select both Captain and Vice-Captain before saving</p>
              )}
            </div>
          )}

          <PlayingXIView
            players={displayPlayers}
            viewMode={teamViewMode}
            onViewModeChange={setTeamViewMode}
            canEdit={!matchLocked && (staged.length < 11 || isEditingTeam)}
            creditsTotal={totalCredits}
            creditsCap={creditsCap}
            captainId={captainId}
            viceCaptainId={viceCaptainId}
            onRemovePlayer={!matchLocked && (staged.length < 11 || isEditingTeam) ? removePlayer : undefined}
            onAddSlot={!matchLocked && (staged.length < 11 || isEditingTeam) && emptySlotsCount > 0 ? () => setShowPlayerPicker(true) : undefined}
            emptySlotsCount={emptySlotsCount}
          />

          {/* Player picker modal - same as Budget League (filters, sort, scrollable list) */}
          <PlayerPickerModal
            isOpen={showPlayerPicker}
            onBack={() => setShowPlayerPicker(false)}
            title={`Add players (${staged.length}/11)`}
            rowAction="add_button"
            creditsCap={creditsCap}
            players={players}
            excludePlayerIds={staged.map((s: any) => s.tournament_player_id ?? s.id)}
            foreignPlayerIds={foreignPlayerIds}
            stagedPicks={staged.map((s: any) => ({
              tournament_player_id: Number(s.tournament_player_id ?? s.id),
              name: s.name ?? s.player_name ?? '',
              role: s.role,
              credits: s.credits,
              team: s.team ?? s.team_name,
              player_id: s.player_id
            }))}
            onRemoveStaged={
              !matchLocked && (staged.length < 11 || isEditingTeam)
                ? (tid) => {
                    const dp = displayPlayers.find((p) => p.tournament_player_id === Number(tid));
                    if (dp) removePlayer(dp);
                  }
                : undefined
            }
            canSelect={(pl) => {
              const availableCredits = creditsCap - totalCredits;
              const canAfford = (parseFloat(pl.credits) || 0) <= availableCredits;
              return canAfford && canAddPlayerForComposition(staged, pl.role ?? '') && canAddUnderForeignLimit(staged, pl);
            }}
            getRowSubtext={(player: any) => {
              const inTeam = staged.some((s: any) => (s.tournament_player_id ?? s.id) === (player.tournament_player_id ?? player.id));
              if (inTeam) return null;
              const availableCredits = creditsCap - totalCredits;
              if ((parseFloat(player.credits) || 0) > availableCredits) return <span className="text-red-400">Over budget</span>;
              return null;
            }}
            onSelect={(pl) => {
              addPlayer(pl);
            }}
          />
        </>
      )}
    </div>
  );
}

/** Role order for sorting (align with Budget: Batsman, WK-Batsman, All-Rounder, Bowler). */
function getRoleOrder(role: string): number {
  switch (role) {
    case 'Batsman': return 1;
    case 'WK-Batsman': return 2;
    case 'All-Rounder': return 3;
    case 'Bowler': return 4;
    default: return 5;
  }
}

/** Short name for grid: "Virat Kohli" -> "V Kohli". Single word unchanged. */
function shortName(name: string | null | undefined): string {
  if (!name || !name.trim()) return '–';
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name.trim();
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first.charAt(0)} ${last}`;
}

/** Role icon for H2H player row. */
function getRoleIcon(role: string): React.ReactNode {
  switch (role) {
    case 'Batsman':
      return (
        <img
          src={`${process.env.PUBLIC_URL || ''}/images/baticon.png`}
          alt="Bat"
          className="inline-block w-6 h-6 sm:w-7 sm:h-7 object-contain shrink-0"
          style={{ display: 'inline-block', verticalAlign: 'middle' }}
        />
      );
    case 'Bowler': return <span className="text-sm">🥎</span>;
    case 'WK-Batsman': return <span className="text-sm">🧤</span>;
    case 'All-Rounder': return <span className="text-sm">🏏</span>;
    default: return <span className="text-xs">👤</span>;
  }
}

/** H2H tab: match selector, series tally, two-column x 11-row player grid (You vs Opponent). */
function H2HTabContent({
  leagueId,
  state,
  loading,
  selectedMatchIndex,
  onSelectMatchIndex
}: {
  leagueId: number;
  state: any;
  loading: boolean;
  selectedMatchIndex: number;
  onSelectMatchIndex: (i: number) => void;
}) {
  const [showResultAnnouncement, setShowResultAnnouncement] = useState(false);

  useEffect(() => {
    if (loading || !state) return;
    const matches = state.matches || [];
    const match = matches[selectedMatchIndex];
    if (!match || match.status !== 'completed' || !match.opponent_visible) return;
    const matchId = match.match_id ?? (match as any)?.id ?? '';
    const myPlayers = (match?.my_snapshot?.players && Array.isArray(match.my_snapshot.players))
      ? [...(match.my_snapshot.players as any[])].sort((a: any, b: any) => getRoleOrder(a.role ?? '') - getRoleOrder(b.role ?? ''))
      : [];
    const oppPlayers = (match?.opponent_snapshot?.players && Array.isArray(match.opponent_snapshot.players))
      ? [...(match.opponent_snapshot.players as any[])].sort((a: any, b: any) => getRoleOrder(a.role ?? '') - getRoleOrder(b.role ?? ''))
      : [];
    const myTotalDisplay = myPlayers.length > 0
      ? myPlayers.reduce((s: number, p: any) => s + (typeof p.total_points === 'number' ? p.total_points : 0), 0)
      : (match?.my_points ?? null);
    const oppTotalDisplay = oppPlayers.length > 0
      ? oppPlayers.reduce((s: number, p: any) => s + (typeof p.total_points === 'number' ? p.total_points : 0), 0)
      : (match?.opponent_points ?? null);
    const myNum = myTotalDisplay != null ? Number(myTotalDisplay) : null;
    const oppNum = oppTotalDisplay != null ? Number(oppTotalDisplay) : null;
    const outcome = myNum != null && oppNum != null ? (myNum > oppNum ? 'won' : myNum < oppNum ? 'lost' : 'draw') : null;
    if (!outcome) return;
    const resultStorageKey = `h2h_result_seen_${leagueId}_${matchId}`;
    if (!localStorage.getItem(resultStorageKey)) {
      setShowResultAnnouncement(true);
    }
  }, [loading, state, leagueId, selectedMatchIndex]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  if (!state) {
    return <p className="text-gray-600 py-6">Could not load H2H state.</p>;
  }
  const matches = state.matches || [];
  const match = matches[selectedMatchIndex];
  const tally = state.series_tally || { my_wins: 0, opponent_wins: 0, my_total_points: 0, opponent_total_points: 0 };
  const isMulti = state.league?.h2h_type === 'multi_match';

  const myPlayers = (match?.my_snapshot?.players && Array.isArray(match.my_snapshot.players))
    ? [...(match.my_snapshot.players as any[])].sort((a, b) => getRoleOrder(a.role ?? '') - getRoleOrder(b.role ?? ''))
    : [];
  const oppPlayers = (match?.opponent_visible && match?.opponent_snapshot?.players && Array.isArray(match.opponent_snapshot.players))
    ? [...(match.opponent_snapshot.players as any[])].sort((a, b) => getRoleOrder(a.role ?? '') - getRoleOrder(b.role ?? ''))
    : [];
  const maxRows = Math.max(myPlayers.length, oppPlayers.length, 11);
  const myTotalFromGrid = myPlayers.reduce((s, p) => s + (typeof (p as any).total_points === 'number' ? (p as any).total_points : 0), 0);
  const oppTotalFromGrid = oppPlayers.reduce((s, p) => s + (typeof (p as any).total_points === 'number' ? (p as any).total_points : 0), 0);
  const myTotalDisplay = myPlayers.length > 0 ? myTotalFromGrid : (match?.my_points ?? null);
  const oppTotalDisplay = oppPlayers.length > 0 ? oppTotalFromGrid : (match?.opponent_points ?? null);

  // One-time result announcement (first visit after match completed)
  const matchId = match?.match_id ?? (match as any)?.id ?? '';
  const isCompleted = match?.status === 'completed' && match?.opponent_visible;
  const myNum = myTotalDisplay != null ? Number(myTotalDisplay) : null;
  const oppNum = oppTotalDisplay != null ? Number(oppTotalDisplay) : null;
  const outcome: 'won' | 'lost' | 'draw' | null = isCompleted && myNum != null && oppNum != null
    ? (myNum > oppNum ? 'won' : myNum < oppNum ? 'lost' : 'draw')
    : null;
  const resultStorageKey = `h2h_result_seen_${leagueId}_${matchId}`;
  const dismissResultAnnouncement = () => {
    localStorage.setItem(resultStorageKey, '1');
    setShowResultAnnouncement(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-primary flex items-center"><span className="mr-3">⚔️</span>Head to Head</h2>
      {/* One-time announcement when user first visits H2H tab after match is completed */}
      {showResultAnnouncement && outcome && (
        <div className={`rounded-xl border-2 p-4 sm:p-5 ${outcome === 'won' ? 'bg-green-50 border-green-200' : outcome === 'lost' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
          {outcome === 'won' && (
            <p className="text-lg sm:text-xl font-bold text-green-800 mb-2">Congratulations! You won this match.</p>
          )}
          {outcome === 'lost' && (
            <p className="text-lg sm:text-xl font-bold text-amber-800 mb-2">You lost. Better luck next time!</p>
          )}
          {outcome === 'draw' && (
            <p className="text-lg sm:text-xl font-bold text-gray-800 mb-2">This match was a draw.</p>
          )}
          <button
            type="button"
            onClick={dismissResultAnnouncement}
            className="mt-2 px-4 py-2 rounded-lg font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Dismiss
          </button>
        </div>
      )}
      {isMulti && (
        <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <span className="text-gray-600">Series: <strong className="text-primary">{tally.my_wins}</strong> – <strong className="text-primary">{tally.opponent_wins}</strong></span>
          <span className="text-gray-600">Total points: You <strong className="text-primary">{tally.my_total_points?.toFixed(1) ?? 0}</strong> – Opponent <strong className="text-primary">{tally.opponent_total_points?.toFixed(1) ?? 0}</strong></span>
        </div>
      )}
      {(matches.length > 1 || (match && match.match_name)) && (
        <div className="flex items-center gap-2 flex-wrap">
          {matches.length > 1 && (
            <>
              <button type="button" onClick={() => onSelectMatchIndex(Math.max(0, selectedMatchIndex - 1))} disabled={selectedMatchIndex === 0} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-primary font-medium rounded-lg border border-gray-200 transition-colors">←</button>
              <button type="button" onClick={() => onSelectMatchIndex(Math.min(matches.length - 1, selectedMatchIndex + 1))} disabled={selectedMatchIndex >= matches.length - 1} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-primary font-medium rounded-lg border border-gray-200 transition-colors">→</button>
            </>
          )}
          <span className="text-gray-600 font-medium">
            {formatMyTeamMatchLabel(match, `Match ${selectedMatchIndex + 1}`)}
          </span>
          {match?.status && (
            <span className={`px-2 py-1 rounded-lg text-sm font-medium ${
              match.status === 'live' ? 'bg-green-100 text-green-800' :
              match.status === 'completed' ? 'bg-gray-100 text-gray-800' : 'bg-amber-100 text-amber-800'
            }`}>
              {match.status.charAt(0).toUpperCase() + match.status.slice(1)}
            </span>
          )}
        </div>
      )}
      {match && (
        <div className="overflow-x-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 min-w-[280px] inline-block w-full">
            {/* Header row: labels + dynamic totals + winner/lost highlight when match completed */}
            <div className="grid grid-cols-2 border-b border-gray-200 bg-gray-50 rounded-t-xl">
              <div className={`p-3 sm:p-4 border-r border-gray-200 flex items-center justify-between gap-2 ${outcome === 'won' ? 'bg-green-50/80' : outcome === 'lost' ? 'bg-gray-100/80' : ''}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-primary text-sm sm:text-base">You</span>
                  {outcome === 'won' && <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-200 text-green-900 shrink-0">Winner</span>}
                  {outcome === 'lost' && <span className="text-gray-500 text-xs shrink-0">Lost</span>}
                </div>
                <div className="font-bold text-primary text-sm sm:text-base tabular-nums shrink-0">
                  {myTotalDisplay != null ? `${myTotalDisplay.toFixed(1)} pts` : '–'}
                </div>
              </div>
              <div className={`p-3 sm:p-4 flex items-center justify-between gap-2 min-w-0 ${outcome === 'lost' ? 'bg-green-50/80' : outcome === 'won' ? 'bg-gray-100/80' : ''}`}>
                <div className="font-bold text-primary text-sm sm:text-base tabular-nums shrink-0">
                  {match.opponent_visible
                    ? (oppTotalDisplay != null ? `${oppTotalDisplay.toFixed(1)} pts` : '–')
                    : '–'}
                </div>
                <div className="flex items-center gap-2 min-w-0 justify-end">
                  <span className="font-semibold text-primary text-sm sm:text-base truncate text-right">
                    {match.opponent_visible ? (state.opponent_display_name || 'Opponent') : 'Opponent'}
                  </span>
                  {outcome === 'lost' && <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-200 text-green-900 shrink-0">Winner</span>}
                  {outcome === 'won' && <span className="text-gray-500 text-xs shrink-0">Lost</span>}
                </div>
              </div>
            </div>
            {/* Player rows: compact grid — left: name (V Kohli) + C/VC then number; right: number then name */}
            {maxRows > 0 ? (
              Array.from({ length: maxRows }, (_, i) => {
                const myP = myPlayers[i];
                const oppP = match.opponent_visible ? oppPlayers[i] : null;
                const ptsStr = (p: any) => (p != null && typeof (p as any).total_points === 'number' ? (p as any).total_points.toFixed(1) : '–');
                return (
                  <div key={i} className="grid grid-cols-2 border-b border-gray-100 last:border-b-0">
                    {/* Left column (You): name (short) + C/VC ... points number only */}
                    <div className="py-1.5 px-2 sm:py-2 sm:px-3 border-r border-gray-100 flex items-center justify-between gap-2 min-w-0">
                      {myP ? (
                        <>
                          <div className="font-medium text-gray-800 text-sm flex items-center gap-1.5 min-w-0">
                            <span className="shrink-0">{getRoleIcon(myP.role ?? '')}</span>
                            <span className="truncate" title={myP.name ?? undefined}>{shortName(myP.name)}</span>
                            {myP.is_captain && <span className="text-yellow-600 font-bold text-xs shrink-0">C</span>}
                            {myP.is_vice_captain && !myP.is_captain && <span className="text-blue-600 font-bold text-xs shrink-0">VC</span>}
                          </div>
                          <span className="font-semibold text-primary text-sm tabular-nums shrink-0">{ptsStr(myP)}</span>
                        </>
                      ) : (
                        <span className="text-gray-400 text-sm">–</span>
                      )}
                    </div>
                    {/* Right column (Opponent): points | player name + C/VC | role icon (mirrors left: role | name | pts) */}
                    <div className="py-1.5 px-2 sm:py-2 sm:px-3 flex items-center gap-2 min-w-0">
                      {match.opponent_visible ? (
                        oppP ? (
                          <>
                            <span className="font-semibold text-primary text-sm tabular-nums shrink-0">{ptsStr(oppP)}</span>
                            <div className="font-medium text-gray-800 text-sm flex items-center gap-1 min-w-0 justify-end flex-1">
                              <span className="truncate text-right" title={oppP.name ?? undefined}>{shortName(oppP.name)}</span>
                              {oppP.is_captain && <span className="text-yellow-600 font-bold text-xs shrink-0">C</span>}
                              {oppP.is_vice_captain && !oppP.is_captain && <span className="text-blue-600 font-bold text-xs shrink-0">VC</span>}
                            </div>
                            <span className="shrink-0">{getRoleIcon(oppP.role ?? '')}</span>
                          </>
                        ) : (
                          <span className="text-gray-400 text-sm">–</span>
                        )
                      ) : (
                        <span className="text-gray-400 text-xs">–</span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-6 text-center text-gray-500 text-sm">
                {match.my_snapshot ? 'No players in snapshot.' : 'Team snapshot not available for this match.'}
              </div>
            )}
          </div>
        </div>
      )}
      {!match && matches.length === 0 && <p className="text-gray-500">No matches in this league.</p>}
    </div>
  );
}

const H2HLeaguePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ?tab=standings (e.g. from My Leagues) opens the H2H scoreboard tab
  const rawTab = searchParams.get('tab');
  const tabFromUrl = (rawTab === 'standings' ? 'h2h' : rawTab) as 'info' | 'myteam' | 'h2h' | null;
  const validTabs = ['info', 'myteam', 'h2h'] as const;
  const defaultTab = (tabFromUrl && validTabs.includes(tabFromUrl as any)) ? tabFromUrl : 'info';
  const [activeTab, setActiveTab] = useState<'info' | 'myteam' | 'h2h'>(defaultTab);
  
  const [league, setLeague] = useState<H2HLeague | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManager, setIsManager] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<{ share: boolean; joinCode: boolean; passcode: boolean }>({
    share: false,
    joinCode: false,
    passcode: false
  });
  const copyResetTimersRef = useRef<Partial<Record<'share' | 'joinCode' | 'passcode', number>>>({});
  
  // League editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedLeagueName, setEditedLeagueName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    if (id && user) {
      loadLeagueData();
    }
  }, [id, user]);

  useEffect(() => {
    const raw = searchParams.get('tab');
    const normalized = (raw === 'standings' ? 'h2h' : raw) as 'info' | 'myteam' | 'h2h' | null;
    if (normalized && validTabs.includes(normalized as any) && normalized !== activeTab) {
      setActiveTab(normalized);
    } else if (!raw && activeTab !== 'info') {
      setSearchParams({ tab: activeTab });
    }
  }, [searchParams, activeTab, setSearchParams]);

  const [h2hState, setH2hState] = useState<any>(null);
  const [h2hStateLoading, setH2hStateLoading] = useState(false);
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(0);
  useEffect(() => {
    if (activeTab !== 'h2h' || !id) return;
    setH2hStateLoading(true);
    apiService.getH2hLeagueState(parseInt(id, 10))
      .then((res: any) => setH2hState(res))
      .catch(() => setH2hState(null))
      .finally(() => setH2hStateLoading(false));
  }, [activeTab, id]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!id) {
        setError('League ID is required');
        setLoading(false);
        return;
      }

      const leagueId = parseInt(id);
      if (isNaN(leagueId)) {
        setError('Invalid league ID');
        setLoading(false);
        return;
      }
      
      console.log('🔍 Loading H2H league data for league ID:', leagueId);
      
      // Load league details
      const leagueData = await apiService.getFantasyLeague(leagueId);
      console.log('🔍 League data loaded:', leagueData);
      setLeague(leagueData.league);
      setIsManager(leagueData.league.created_by === user?.id);
      setCurrentUserId(user?.id || null);
      setEditedLeagueName(leagueData.league.name);

      // Load participants
      const participantsData = await apiService.getFantasyLeagueParticipants(leagueId);
      setParticipants(participantsData.participants);

    } catch (err: any) {
      console.error('🔍 Error loading H2H league data:', err);
      setError(err.message || 'Failed to load league data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const showNotification = (message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const copyWithFeedback = async (
    value: string | null | undefined,
    key: 'share' | 'joinCode' | 'passcode',
    successMessage: string
  ) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback((prev) => ({ ...prev, [key]: true }));
      showNotification(successMessage);
      const existing = copyResetTimersRef.current[key];
      if (existing) window.clearTimeout(existing);
      copyResetTimersRef.current[key] = window.setTimeout(() => {
        setCopyFeedback((prev) => ({ ...prev, [key]: false }));
      }, 1800);
    } catch {
      showNotification('Copy failed. Please try again.');
    }
  };

  useEffect(() => {
    return () => {
      (Object.values(copyResetTimersRef.current) as number[]).forEach((id) => {
        if (id) window.clearTimeout(id);
      });
    };
  }, []);

  const handleRenameLeague = async (newName: string) => {
    if (!league || !newName.trim() || newName.trim() === league.name) {
      return;
    }

    setIsRenaming(true);
    try {
      const result = await apiService.updateFantasyLeagueName(league.id, newName.trim());
      if (result.success) {
        setLeague({ ...league, name: newName.trim() });
        showNotification('League name updated successfully!');
      } else {
        showNotification(result.message || 'Failed to update league name');
      }
    } catch (err: any) {
      console.error('Error renaming league:', err);
      showNotification(err.message || 'Failed to update league name');
    } finally {
      setIsRenaming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Error Loading League</h2>
          <p className="text-red-300 mb-4">{error || 'League not found'}</p>
          <button
            onClick={() => navigate('/my-leagues')}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
          >
            Back to My Leagues
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-2 sm:py-6 px-0 sm:px-4">
      {/* Header - match Budget League */}
      <div className="bg-gradient-to-r from-primary to-secondary p-2 sm:p-4 md:p-5 mb-3 sm:mb-6">
        <div className="w-full px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate('/my-leagues')}
                className="text-blue-100 hover:text-white transition-colors duration-200 mb-1 sm:mb-2 flex items-center text-xs sm:text-sm"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Back to Leagues</span>
                <span className="sm:hidden">Back</span>
              </button>
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-accent mb-1 truncate">{league.name}</h1>
              <p className="text-blue-100 text-xs sm:text-sm md:text-base">
                <span className="truncate block sm:inline">{league.tournament_name}</span>
                <span className="hidden sm:inline"> · {league.current_participants}/{league.max_participants} Participants</span>
              </p>
            </div>
            <div className="text-left sm:text-right flex-shrink-0">
              <div className="inline-flex items-center space-x-1 sm:space-x-2">
                <span className="text-blue-100 text-xs sm:text-sm">Participants:</span>
                <span className="text-white text-xs sm:text-sm md:text-base font-semibold">{league.current_participants}/{league.max_participants}</span>
              </div>
              <span className={`ml-2 inline-block px-3 py-1 rounded-full text-sm font-medium ${
                league.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {league.status.charAt(0).toUpperCase() + league.status.slice(1)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs - match Budget League */}
      <div className="w-full px-1.5 sm:px-4 lg:px-8 py-2 sm:py-4 md:py-5">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 border border-white/20 mb-6 sm:mb-8">
          <div className="flex space-x-1">
            {[
              { key: 'info', label: 'League Info', shortLabel: 'Info' },
              { key: 'myteam', label: 'My Team', shortLabel: 'Team' },
              { key: 'h2h', label: 'H2H', shortLabel: 'H2H' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key as any)}
                className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-3 md:px-4 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-accent text-primary shadow-lg transform scale-105'
                    : 'text-white hover:text-accent hover:bg-white/10'
                }`}
              >
                <span className="mr-1 sm:mr-2">{tab.key === 'info' ? 'ℹ️' : tab.key === 'myteam' ? '👥' : '⚔️'}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content - white box like Budget League */}
      <div className="w-full px-0 sm:px-4 lg:px-8">
        <div className="bg-white rounded-none sm:rounded-xl p-3 sm:p-6 shadow-none sm:shadow-sm border-0 sm:border border-gray-200">
          {activeTab === 'info' && (
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-none sm:rounded-xl p-3 sm:p-6 shadow-none sm:shadow-sm border-0 sm:border border-gray-200">
                <h2 className="text-2xl font-bold text-primary flex items-center mb-6">
                  <span className="mr-3">🏆</span>League Details
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">League Name:</span>
                    <div className="flex items-center space-x-2">
                      {isEditingName ? (
                        <>
                          <input
                            type="text"
                            value={editedLeagueName}
                            onChange={(e) => setEditedLeagueName(e.target.value)}
                            className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-primary font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
                            autoFocus
                          />
                          <button
                            onClick={async () => {
                              if (editedLeagueName.trim() && editedLeagueName.trim() !== league.name) {
                                await handleRenameLeague(editedLeagueName.trim());
                              }
                              setIsEditingName(false);
                            }}
                            disabled={isRenaming || !editedLeagueName.trim() || editedLeagueName.trim() === league.name}
                            className="text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Save"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingName(false);
                              setEditedLeagueName(league.name);
                            }}
                            className="text-red-600 hover:text-red-700 transition-colors"
                            title="Cancel"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-primary font-semibold">{league.name}</span>
                          {isManager && (
                            <button
                              onClick={() => {
                                setEditedLeagueName(league.name);
                                setIsEditingName(true);
                              }}
                              className="text-accent hover:text-accent/80 transition-colors ml-2"
                              title="Edit league name"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Participants:</span>
                    <span className="text-primary font-semibold">{league.current_participants}/{league.max_participants} (Fixed at 2 for H2H)</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Tournament:</span>
                    <span className="text-primary font-semibold">{league.tournament_name}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">League Type:</span>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">H2H</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      league.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {league.status.charAt(0).toUpperCase() + league.status.slice(1)}
                    </span>
                  </div>
                  {league.join_code && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600">Join Code:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-primary font-mono bg-gray-100 px-3 py-1 rounded border border-gray-200">
                          {league.join_code}
                        </span>
                        <button
                          onClick={() => copyWithFeedback(league.join_code, 'joinCode', 'Join code copied!')}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border transition-all duration-200 cursor-pointer active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                            copyFeedback.joinCode
                              ? 'border-green-300 bg-green-50 text-green-700'
                              : 'border-gray-300 bg-white text-accent hover:text-accent/80 hover:bg-gray-50'
                          }`}
                          title="Copy join code"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <span className="text-xs font-medium">{copyFeedback.joinCode ? 'Copied' : 'Copy'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                  {league.passcode && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600">League Passcode:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-primary font-mono bg-gray-100 px-3 py-1 rounded border border-gray-200">
                          {league.passcode}
                        </span>
                        <button
                          onClick={() => copyWithFeedback(league.passcode, 'passcode', 'Passcode copied!')}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border transition-all duration-200 cursor-pointer active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                            copyFeedback.passcode
                              ? 'border-green-300 bg-green-50 text-green-700'
                              : 'border-gray-300 bg-white text-accent hover:text-accent/80 hover:bg-gray-50'
                          }`}
                          title="Copy passcode"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <span className="text-xs font-medium">{copyFeedback.passcode ? 'Copied' : 'Copy'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-none sm:rounded-xl p-3 sm:p-6 shadow-none sm:shadow-sm border-0 sm:border border-gray-200">
                <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <span className="mr-3">👥</span>Participants ({participants.length})
                </h2>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {participants.length > 0 ? (
                    participants.map((participant, index) => (
                      <div key={participant.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <p className="text-primary font-medium">{participant.display_name}</p>
                            {participant.joined_at && (
                              <p className="text-gray-500 text-sm">{new Date(participant.joined_at).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                        {participant.user_id === currentUserId && (
                          <span className="px-2 py-1 bg-accent text-primary text-xs font-medium rounded-full">You</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No participants yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'info' && (league.join_code || league.passcode) && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-primary font-semibold mb-2 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                Share League
              </h4>
              <p className="text-gray-600 text-sm mb-3">
                Share this link with your opponent. They'll be able to join after logging in or signing up.
              </p>
              {(() => {
                const shareUrl = `${window.location.origin}/join-league?code=${league.join_code || ''}&passcode=${league.passcode || ''}&type=h2h`;
                return (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        readOnly
                        value={shareUrl}
                        className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded text-gray-800 text-sm font-mono"
                      />
                      <button
                        onClick={() => copyWithFeedback(shareUrl, 'share', 'Link copied to clipboard!')}
                        className={`px-4 py-2 rounded border transition-all duration-200 flex items-center space-x-2 cursor-pointer active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                          copyFeedback.share
                            ? 'bg-green-100 border-green-300 text-green-800'
                            : 'bg-accent hover:bg-accent/90 border-transparent text-primary'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span className="font-medium">{copyFeedback.share ? 'Copied!' : 'Copy Link'}</span>
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {league.join_code && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          Join Code: {league.join_code}
                        </span>
                      )}
                      {league.passcode && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          Passcode: {league.passcode}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'info' && league.live_tournament_id && (
            <div className="mt-6 bg-gray-900 rounded-xl p-6 border border-gray-700">
              <FantasyPointsSummary tournamentId={league.live_tournament_id} />
            </div>
          )}

          {activeTab === 'myteam' && (
            <H2HMyTeamTab leagueId={league.id} tournamentId={league.live_tournament_id} userId={currentUserId} onNotify={showNotification} h2hStatus={league.h2h_status} />
          )}

          {activeTab === 'h2h' && (
            <H2HTabContent
              leagueId={league.id}
              state={h2hState}
              loading={h2hStateLoading}
              selectedMatchIndex={selectedMatchIndex}
              onSelectMatchIndex={setSelectedMatchIndex}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default H2HLeaguePage;

